import React, { useMemo, useState } from "react";

const PHASE_NAMES = [
  "New Moon",
  "Waxing Crescent",
  "First Quarter",
  "Waxing Gibbous",
  "Full Moon",
  "Waning Gibbous",
  "Last Quarter",
  "Waning Crescent",
];

export function getMoonPhase(date = new Date()) {
  const synodic = 29.530588853;
  // Reference new moon: 2000-01-06 18:14 UTC
  const ref = Date.UTC(2000, 0, 6, 18, 14, 0);
  const days = (date.getTime() - ref) / 86400000;
  let age = days % synodic;
  if (age < 0) age += synodic;
  const frac = age / synodic; // 0 = new, 0.5 = full
  const illum = (1 - Math.cos(2 * Math.PI * frac)) / 2; // 0..1..0
  const waxing = frac < 0.5;
  const idx = Math.floor(frac * 8 + 0.5) % 8;
  return { age, frac, illum, waxing, name: PHASE_NAMES[idx] };
}

// Accurate CSS/SVG moon glyph: gold-lit portion over a dark disc.
export const MoonGlyph = ({ size = 22, illum, waxing }) => {
  const R = 48;
  const termRx = R * Math.abs(1 - 2 * illum);
  const litColor = "#E5C365";
  const darkColor = "#2b2d44";
  const ellipseFill = illum < 0.5 ? darkColor : litColor;
  const litRectX = waxing ? 50 : 2; // right half if waxing, left if waning
  const id = useMemo(() => `m${Math.random().toString(36).slice(2, 8)}`, []);
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <defs>
        <clipPath id={id}>
          <circle cx="50" cy="50" r={R} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${id})`}>
        <rect x="0" y="0" width="100" height="100" fill={darkColor} />
        <rect x={litRectX} y="0" width="48" height="100" fill={litColor} />
        <ellipse cx="50" cy="50" rx={termRx} ry={R} fill={ellipseFill} />
      </g>
      <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(217,164,65,0.55)" strokeWidth="3" />
    </svg>
  );
};

export const MoonPhase = () => {
  const [open, setOpen] = useState(false);
  const phase = useMemo(() => getMoonPhase(), []);
  const pct = Math.round(phase.illum * 100);

  return (
    <div
      className="relative flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="moon-phase-widget"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Moon phase: ${phase.name}, ${pct}% illuminated`}
        className="flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-[#131422]"
      >
        <MoonGlyph size={22} illum={phase.illum} waxing={phase.waxing} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 z-50 w-52 rounded-xl border border-[#D9A441]/25 bg-[#131422]/95 p-4 text-center shadow-[0_10px_40px_rgba(0,0,0,0.5)] backdrop-blur-xl"
          data-testid="moon-phase-popover"
        >
          <div className="mx-auto mb-2 flex items-center justify-center">
            <MoonGlyph size={44} illum={phase.illum} waxing={phase.waxing} />
          </div>
          <p className="font-display text-lg font-semibold text-[#F8F3E6]" data-testid="moon-phase-name">
            {phase.name}
          </p>
          <p className="mt-0.5 text-sm text-[#A3A5B8]" data-testid="moon-phase-illum">
            {pct}% illuminated
          </p>
        </div>
      )}
    </div>
  );
};
